import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { decodeHash, isRowToggle } from './useHashAccordion';

/** `?id=~123` — a pin in the old syntax, which useLinkedPin reads, not a filter */
export const isLegacyPin = (id: string | null) => !!id && id.startsWith('~');

/** the row a navigation brought a link to, or '' when it brought none */
const incomingPin = (location: { pathname: string; hash: string; search: string }) => {
    const { hash, search } = location;
    // the reader opening a row by clicking it is not a link to it
    if (isRowToggle(location)) return '';
    const token = decodeHash(hash).trim();
    if (token) return token;
    const legacy = new URLSearchParams(search).get('id');
    return isLegacyPin(legacy) ? legacy!.slice(1) : '';
};

/**
 * The row an incoming link names, as the list's `pin` filter: give it to the
 * page's ListBase `filter`, and the API puts that row first on every page —
 * whatever page it would otherwise be on, and even if the list's filters
 * leave it out (PinFilter). useHashAccordion then opens it as it does any row
 * named in the hash.
 *
 * The same token as the hash, so it pins by id or by name, whichever the
 * list's links use. Followed for every link that arrives — when the list
 * opens, and when one arrives while it is already showing (a notification
 * clicked from the list itself) — and for nothing else:
 *
 * - a row the reader opens by clicking it is not a link (useHashAccordion
 *   notes those — see isRowToggle), and does not repin;
 * - react-admin drops the hash as soon as it rewrites the list's params, and
 *   the pin outlives that — a pin that came and went between pages would
 *   order them differently, and the pinned row would turn up twice or not
 *   at all.
 *
 * A new pin is a new list: key the page's ListBase on it, so the list starts
 * over at its first page under the new pin rather than splicing that page
 * onto pages fetched under the old one.
 *
 * Links written before `pin` existed pinned with `?id=~123`, and are still in
 * emails and bookmarks. The API no longer reads that, so it is taken as the
 * pin here — see isLegacyPin for the lists that also read a plain `?id=`.
 */
const useLinkedPin = (): { pin?: string } => {
    const location = useLocation();
    const [pin, setPin] = useState(() => incomingPin(location));
    // the navigation already read — a location is classified once, when it
    // arrives, and a later render of it is not a later link
    const [handled, setHandled] = useState(location);

    let current = pin;
    if (location !== handled) {
        setHandled(location);
        // a link that arrived: taken as the pin in this same render, so the
        // list never fetches once more under the old one
        const arrived = incomingPin(location);
        if (arrived && arrived !== pin) {
            setPin(arrived);
            current = arrived;
        }
    }
    return current ? { pin: current } : {};
};

export default useLinkedPin;
